import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { AppShell } from '@/components/shared/AppShell'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ArrowLeft, Save, Eye, Edit3 } from 'lucide-react'
import { CoverImageUploader } from '@/components/admin/CoverImageUploader'

interface PostForm {
  id: number
  title: string
  slug: string
  excerpt: string
  content: string
  cover_image_url: string
  author_name: string
  category: string
  exam_tags: string[]
  status: string
  seo_title: string
  seo_description: string
  seo_keywords: string
}

interface Props {
  post: PostForm
  categories: string[]
}

const CATEGORY_LABELS: Record<string, string> = {
  announcement:     'Announcements',
  exam_guide:       'Exam Guide',
  preparation_tips: 'Prep Tips',
  general:          'General',
}

export default function BlogPostsEdit({ post, categories }: Props) {
  const [title, setTitle] = useState(post.title)
  const [slug, setSlug] = useState(post.slug)
  const [excerpt, setExcerpt] = useState(post.excerpt)
  const [content, setContent] = useState(post.content)
  const [coverImageUrl, setCoverImageUrl] = useState(post.cover_image_url)
  const [authorName, setAuthorName] = useState(post.author_name)
  const [category, setCategory] = useState(post.category)
  const [examTags, setExamTags] = useState(post.exam_tags.join(', '))
  const [seoTitle, setSeoTitle] = useState(post.seo_title)
  const [seoDescription, setSeoDescription] = useState(post.seo_description)
  const [seoKeywords, setSeoKeywords] = useState(post.seo_keywords)
  const [previewTab, setPreviewTab] = useState<'edit' | 'preview'>('edit')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || !excerpt.trim() || !content.trim()) {
      alert('Title, Excerpt, and Content are required.')
      return
    }
    setSubmitting(true)
    router.patch(
      `/admin/blog_posts/${post.id}`,
      {
        blog_post: {
          title: title.trim(),
          slug: slug.trim(),
          excerpt: excerpt.trim(),
          content: content.trim(),
          cover_image_url: coverImageUrl.trim(),
          author_name: authorName.trim(),
          category,
          exam_tags: examTags.split(',').map(t => t.trim()).filter(Boolean),
          seo_title: seoTitle.trim(),
          seo_description: seoDescription.trim(),
          seo_keywords: seoKeywords.trim(),
        },
      },
      { onFinish: () => setSubmitting(false) }
    )
  }

  return (
    <AppShell>
      <div className="space-y-6 max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b border-border">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm" className="h-9 w-9 p-0">
              <Link href={`/admin/blog_posts/${post.id}`}><ArrowLeft className="w-4 h-4" /></Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">Edit Blog Post</h1>
              <p className="text-xs text-muted-foreground mt-0.5 font-mono">/blog/{post.slug}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="border-border bg-card shadow-xs">
            <CardHeader className="px-6 py-4 border-b border-border">
              <CardTitle className="text-base font-bold text-foreground">Post Details</CardTitle>
              <CardDescription className="text-xs text-muted-foreground">Title, category, author, and cover image.</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="sm:col-span-2 space-y-2">
                  <Label htmlFor="title" className="text-xs font-semibold text-foreground">Title *</Label>
                  <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} required className="text-sm" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category" className="text-xs font-semibold text-foreground">Category *</Label>
                  <select id="category" value={category} onChange={(e) => setCategory(e.target.value)} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring">
                    {categories.map((cat) => (<option key={cat} value={cat}>{CATEGORY_LABELS[cat] || cat}</option>))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="slug" className="text-xs font-semibold text-foreground">URL Slug *</Label>
                  <Input id="slug" value={slug} onChange={(e) => setSlug(e.target.value)} required className="font-mono text-sm" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="author_name" className="text-xs font-semibold text-foreground">Author Name</Label>
                  <Input id="author_name" value={authorName} onChange={(e) => setAuthorName(e.target.value)} className="text-sm" />
                </div>
              </div>
              <CoverImageUploader
                value={coverImageUrl}
                onChange={setCoverImageUrl}
                disabled={submitting}
              />
              <div className="space-y-2">
                <Label htmlFor="exam_tags" className="text-xs font-semibold text-foreground">Exam Tags</Label>
                <Input id="exam_tags" value={examTags} onChange={(e) => setExamTags(e.target.value)} placeholder="GATE, SBI Clerk (comma-separated)" className="text-sm" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="excerpt" className="text-xs font-semibold text-foreground">Excerpt * <span className="font-normal text-muted-foreground">(max 300 chars)</span></Label>
                <textarea id="excerpt" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} rows={3} maxLength={300} required className="w-full text-sm p-3 rounded-xl border border-border bg-background text-foreground leading-relaxed focus:outline-none focus:ring-1 focus:ring-primary resize-none" />
                <p className="text-[11px] text-muted-foreground">{excerpt.length}/300</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-xs">
            <CardHeader className="px-6 py-4 border-b border-border flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base font-bold text-foreground">Content *</CardTitle>
                <CardDescription className="text-xs text-muted-foreground">Full article body. HTML supported.</CardDescription>
              </div>
              <div className="flex items-center gap-1 bg-muted p-1 rounded-lg border border-border">
                <button type="button" onClick={() => setPreviewTab('edit')} className={`flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-md transition-colors ${previewTab === 'edit' ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'}`}><Edit3 className="w-3.5 h-3.5" /><span>Editor</span></button>
                <button type="button" onClick={() => setPreviewTab('preview')} className={`flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-md transition-colors ${previewTab === 'preview' ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'}`}><Eye className="w-3.5 h-3.5" /><span>Preview</span></button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {previewTab === 'edit' ? (
                <textarea id="content" value={content} onChange={(e) => setContent(e.target.value)} rows={22} required className="w-full font-mono text-xs sm:text-sm p-4 rounded-xl border border-border bg-background text-foreground leading-relaxed focus:outline-none focus:ring-1 focus:ring-primary resize-y" />
              ) : (
                <div className="p-6 rounded-xl border border-border bg-background min-h-[400px] max-h-[500px] overflow-y-auto text-foreground leading-relaxed text-sm" dangerouslySetInnerHTML={{ __html: content || '<p>Nothing to preview.</p>' }} />
              )}
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-xs">
            <CardHeader className="px-6 py-4 border-b border-border">
              <CardTitle className="text-base font-bold text-foreground">SEO Settings <span className="text-xs font-normal text-muted-foreground">(optional)</span></CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <div className="space-y-2"><Label htmlFor="seo_title" className="text-xs font-semibold text-foreground">SEO Title</Label><Input id="seo_title" value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} className="text-sm" /></div>
              <div className="space-y-2"><Label htmlFor="seo_description" className="text-xs font-semibold text-foreground">Meta Description</Label><Input id="seo_description" value={seoDescription} onChange={(e) => setSeoDescription(e.target.value)} className="text-sm" /></div>
              <div className="space-y-2"><Label htmlFor="seo_keywords" className="text-xs font-semibold text-foreground">Keywords</Label><Input id="seo_keywords" value={seoKeywords} onChange={(e) => setSeoKeywords(e.target.value)} className="text-sm" /></div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-xs">
            <CardContent className="p-6 flex justify-end gap-3">
              <Button asChild variant="outline"><Link href={`/admin/blog_posts/${post.id}`}>Cancel</Link></Button>
              <Button type="submit" disabled={submitting} className="gap-2 font-semibold">
                <Save className="w-4 h-4" />{submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </CardContent>
          </Card>
        </form>
      </div>
    </AppShell>
  )
}
